import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatNumber, getInitials } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import PostCard from '../components/feed/PostCard';
import { ArrowLeft, Settings, Grid3X3, Loader2, UserPlus, UserMinus, Camera, Play, Image, Circle, Star, Crown, Check, X, Sparkles, Coins, Send, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { ServicesSection } from '../components/appointments/ServicesSection';
import { BookingModal } from '../components/appointments/BookingModal';
import { HighlightsBar } from '../components/stories/StoryHighlights';
import { VerifiedBadge } from '../components/ui/verified-badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick coin amounts for sending
const QUICK_AMOUNTS = [10, 25, 50, 100, 250, 500];

export default function ProfilePage() {
  const { username } = useParams();
  const { user: currentUser, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  
  // Send Coins state
  const [showSendCoinsModal, setShowSendCoinsModal] = useState(false);
  const [coinAmount, setCoinAmount] = useState('');
  const [sendingCoins, setSendingCoins] = useState(false);
  const [myCoinsBalance, setMyCoinsBalance] = useState(0);
  
  // Booking state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [userServices, setUserServices] = useState([]);

  const targetUsername = username || currentUser?.username;
  const isOwnProfile = targetUsername === currentUser?.username;

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users/username/${targetUsername}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setFollowing(response.data.is_following);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('User not found');
    }
  }, [targetUsername, token]);

  const fetchPosts = useCallback(async () => {
    if (!profile) return;
    try {
      const response = await axios.get(`${API}/posts/user/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allPosts = response.data;
      setPosts(allPosts);
      
      // Filter videos and photos
      setVideos(allPosts.filter(p => p.media_type === 'video'));
      setPhotos(allPosts.filter(p => p.media_type === 'image' || (p.media && p.media.length > 0 && p.media_type !== 'video')));
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  }, [profile, token]);

  useEffect(() => {
    setLoading(true);
    fetchProfile().finally(() => setLoading(false));
  }, [fetchProfile]);

  useEffect(() => {
    if (profile) {
      fetchPosts();
      if (!isOwnProfile) {
        fetchSubscriptionInfo();
        fetchMyCoinsBalance();
      }
    }
  }, [profile, fetchPosts]);

  const fetchMyCoinsBalance = async () => {
    try {
      const response = await axios.get(`${API}/coins/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyCoinsBalance(response.data.coins);
    } catch (error) {
      // Ignore
    }
  };

  const fetchUserServices = async () => {
    if (!profile?.id) return;
    try {
      const response = await axios.get(`${API}/users/${profile.id}/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserServices(response.data.services || []);
    } catch (error) {
      // Ignore
    }
  };

  useEffect(() => {
    if (profile && !isOwnProfile) {
      fetchUserServices();
    }
  }, [profile, isOwnProfile]);

  const handleBookService = (service) => {
    setSelectedService(service);
    setShowBookingModal(true);
  };

  const handleSendCoins = async () => {
    const amount = parseInt(coinAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (amount > myCoinsBalance) {
      toast.error('Not enough coins!');
      return;
    }

    setSendingCoins(true);
    try {
      await axios.post(
        `${API}/coins/send`,
        { receiver_id: profile.id, amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Sent ${amount} coins to @${profile.username}!`);
      setMyCoinsBalance(prev => prev - amount);
      setCoinAmount('');
      setShowSendCoinsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send coins');
    } finally {
      setSendingCoins(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    if (!profile) return;
    try {
      // Get creator's subscription tiers
      const tiersResponse = await axios.get(`${API}/creator/${profile.id}/tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionTiers(tiersResponse.data.tiers || []);

      // Check if current user is subscribed to this creator
      const subsResponse = await axios.get(`${API}/subscriptions/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activeSub = subsResponse.data.find(sub => sub.creator_id === profile.id && sub.is_active);
      if (activeSub) {
        setIsSubscribed(true);
        setSubscriptionInfo(activeSub);
      }
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
    }
  };

  const handleSubscribe = async (tierId) => {
    setSubscribing(true);
    try {
      const response = await axios.post(
        `${API}/subscribe/${profile.id}/paypal?tier_id=${tierId}&origin_url=${window.location.origin}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if PayPal redirect is needed (real payment)
      if (response.data.approval_url) {
        // Redirect to PayPal for payment
        toast.info('Redirecting to PayPal for payment...');
        window.location.href = response.data.approval_url;
        return;
      }
      
      // Demo mode - subscription is instant
      if (response.data.demo_mode) {
        toast.success('Subscription successful! Thank you for supporting this creator.');
        setIsSubscribed(true);
        setSubscriptionInfo(response.data.subscription);
        setShowSubscribeModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleFollow = async () => {
    try {
      if (following) {
        await axios.delete(`${API}/users/${profile.id}/follow`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFollowing(false);
        setProfile(prev => ({ ...prev, followers_count: prev.followers_count - 1 }));
      } else {
        await axios.post(`${API}/users/${profile.id}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFollowing(true);
        setProfile(prev => ({ ...prev, followers_count: prev.followers_count + 1 }));
      }
    } catch (error) {
      toast.error(tr('error'));
    }
  };

  const handlePostUpdate = (postId, updates) => {
    setPosts(prev => prev.map(post => 
      post.id === postId ? { ...post, ...updates } : post
    ));
  };

  const handlePostDelete = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
    setProfile(prev => ({ ...prev, posts_count: prev.posts_count - 1 }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-lg">User not found</p>
        <Button onClick={() => navigate(-1)} variant="ghost" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {tr('back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="profile-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-bold text-lg">{profile.display_name || profile.username}</h1>
            <p className="text-sm text-muted-foreground">{profile.posts_count} {tr('posts')}</p>
          </div>
        </div>
      </header>

      {/* Profile Info */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="relative">
            <Avatar className="w-24 h-24 ring-4 ring-primary/20">
              <AvatarImage src={profile.avatar} alt={profile.display_name} />
              <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                {getInitials(profile.display_name || profile.username)}
              </AvatarFallback>
            </Avatar>
            {/* Online Status Indicator */}
            {!isOwnProfile && profile.is_online && (
              <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background" title="Online" />
            )}
            {isOwnProfile && (
              <Link 
                to="/edit-profile" 
                className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors"
                data-testid="change-avatar-btn"
              >
                <Camera className="w-4 h-4" />
              </Link>
            )}
          </div>

          {isOwnProfile ? (
            <Link to="/edit-profile">
              <Button variant="outline" className="rounded-full" data-testid="edit-profile-btn">
                <Settings className="w-4 h-4 mr-2" />
                {tr('editProfile')}
              </Button>
            </Link>
          ) : (
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleFollow}
                variant={following ? "outline" : "default"}
                className={`rounded-full min-w-[120px] ${!following ? 'bg-primary hover:bg-primary/90' : ''}`}
                data-testid="follow-btn"
              >
                {following ? (
                  <>
                    <UserMinus className="w-4 h-4 mr-2" />
                    Unfollow
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Follow
                  </>
                )}
              </Button>
              
              {/* Subscribe Button - Only show if creator has tiers */}
              {subscriptionTiers.length > 0 && (
                <Button
                  onClick={() => isSubscribed ? null : setShowSubscribeModal(true)}
                  variant={isSubscribed ? "outline" : "default"}
                  className={`rounded-full min-w-[120px] ${isSubscribed ? 'border-yellow-500 text-yellow-500' : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white'}`}
                  data-testid="subscribe-btn"
                  disabled={isSubscribed}
                >
                  {isSubscribed ? (
                    <>
                      <Crown className="w-4 h-4 mr-2" />
                      Subscribed
                    </>
                  ) : (
                    <>
                      <Star className="w-4 h-4 mr-2" />
                      Subscribe
                    </>
                  )}
                </Button>
              )}
              
              {/* Send Coins Button */}
              <Button
                onClick={() => setShowSendCoinsModal(true)}
                variant="outline"
                className="rounded-full min-w-[120px] border-pink-500/50 text-pink-500 hover:bg-pink-500/10"
                data-testid="send-coins-btn"
              >
                <Coins className="w-4 h-4 mr-2" />
                Send Coins
              </Button>
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold">{profile.display_name || profile.username}</h2>
            {/* Verified Badge - 50K+ followers */}
            {profile.is_verified && (
              <VerifiedBadge 
                isVerified={true} 
                verificationType={profile.verification_type || 'verified'}
                size="md"
              />
            )}
            {/* Online Status Badge */}
            {!isOwnProfile && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${profile.is_online ? 'bg-green-500/20 text-green-500' : 'bg-muted text-muted-foreground'}`}>
                {profile.is_online ? 'Online' : 'Offline'}
              </span>
            )}
          </div>
          <p className="text-muted-foreground">@{profile.username}</p>
          {profile.bio && <p className="mt-2">{profile.bio}</p>}
        </div>

        {/* Stats */}
        <div className="flex gap-6 mt-4">
          <Link to={`/profile/${profile.username}/followers`} className="hover:underline">
            <span className="font-bold">{formatNumber(profile.followers_count)}</span>
            <span className="text-muted-foreground ml-1">{tr('followers')}</span>
          </Link>
          <Link to={`/profile/${profile.username}/following`} className="hover:underline">
            <span className="font-bold">{formatNumber(profile.following_count)}</span>
            <span className="text-muted-foreground ml-1">{tr('following')}</span>
          </Link>
        </div>
        
        {/* Services Section - For profile owner */}
        {isOwnProfile && (
          <ServicesSection userId={profile.id} isOwner={true} />
        )}
        
        {/* Services Section - For visitors with booking option */}
        {!isOwnProfile && userServices.length > 0 && (
          <div className="mt-6" data-testid="visitor-services-section">
            <h3 className="font-semibold text-lg mb-4">{tr('service')} & {tr('appointments')}</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {userServices.map(service => (
                <div 
                  key={service.id}
                  className="bg-card border rounded-xl p-4 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-sm">{service.name}</h4>
                      <p className="text-xs text-muted-foreground">{service.duration_minutes} {tr('minutes')}</p>
                    </div>
                    <span className={service.is_free ? "text-green-500 font-bold" : "text-pink-500 font-bold"}>
                      {service.is_free ? tr('free') : `$${service.price}`}
                    </span>
                  </div>
                  {service.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{service.description}</p>
                  )}
                  <Button 
                    size="sm" 
                    className="w-full rounded-full"
                    onClick={() => handleBookService(service)}
                    data-testid={`book-service-btn-${service.id}`}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {tr('bookAppointment')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Story Highlights */}
      <div className="border-t border-border/30">
        <HighlightsBar userId={profile.id} isOwn={isOwnProfile} />
      </div>

      {/* Posts */}
      <Tabs defaultValue="posts" className="w-full" onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start border-b border-border/50 rounded-none bg-transparent h-12 p-0">
          <TabsTrigger 
            value="posts" 
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            <Grid3X3 className="w-4 h-4 mr-2" />
            Posts ({posts.length})
          </TabsTrigger>
          <TabsTrigger 
            value="videos" 
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            <Play className="w-4 h-4 mr-2" />
            Videos ({videos.length})
          </TabsTrigger>
          <TabsTrigger 
            value="photos" 
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            <Image className="w-4 h-4 mr-2" />
            Photos ({photos.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="posts" className="m-0">
          <div className="divide-y divide-border/40">
            {posts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <p className="text-muted-foreground">No posts yet</p>
              </div>
            ) : (
              posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  onUpdate={handlePostUpdate}
                  onDelete={handlePostDelete}
                />
              ))
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="videos" className="m-0">
          <div className="grid grid-cols-3 gap-1">
            {videos.length === 0 ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20">
                <Play className="w-12 h-12 text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No videos yet</p>
              </div>
            ) : (
              videos.map(post => (
                <Link 
                  key={post.id} 
                  to={`/post/${post.id}`}
                  className="aspect-square bg-secondary relative group overflow-hidden"
                >
                  {post.media && post.media[0] && (
                    <video 
                      src={post.media[0]} 
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute bottom-1 left-1">
                    <Play className="w-4 h-4 text-white" />
                  </div>
                </Link>
              ))
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="photos" className="m-0">
          <div className="grid grid-cols-3 gap-1">
            {photos.length === 0 ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20">
                <Image className="w-12 h-12 text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No photos yet</p>
              </div>
            ) : (
              photos.map(post => (
                <Link 
                  key={post.id} 
                  to={`/post/${post.id}`}
                  className="aspect-square bg-secondary relative group overflow-hidden"
                >
                  {post.media && post.media[0] && (
                    <img 
                      src={post.media[0]} 
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-sm">{formatNumber(post.likes_count)} ❤️</p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Subscription Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowSubscribeModal(false)}>
          <div 
            className="bg-background rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="subscribe-modal"
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-background p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={profile.avatar} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(profile.display_name || profile.username)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-bold">Subscribe to {profile.display_name || profile.username}</h3>
                  <p className="text-sm text-muted-foreground">@{profile.username}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setShowSubscribeModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Subscription Tiers */}
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2 text-center justify-center">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                <p className="text-sm text-muted-foreground">Choose a subscription tier to support this creator</p>
              </div>

              {subscriptionTiers.map((tier, index) => (
                <Card 
                  key={tier.id || index}
                  className={`cursor-pointer transition-all hover:border-primary ${
                    index === 0 ? 'border-2 border-primary' : ''
                  }`}
                  data-testid={`tier-${tier.id || index}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          {index === 0 && <Star className="w-4 h-4 text-yellow-500" />}
                          {index === 1 && <Crown className="w-4 h-4 text-purple-500" />}
                          {index === 2 && <Sparkles className="w-4 h-4 text-pink-500" />}
                          <h4 className="font-bold text-lg">{tier.name}</h4>
                        </div>
                        <p className="text-2xl font-bold mt-1">
                          ${tier.price}<span className="text-sm font-normal text-muted-foreground">/month</span>
                        </p>
                      </div>
                      {index === 0 && (
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">Popular</span>
                      )}
                    </div>

                    {/* Benefits */}
                    {tier.benefits && tier.benefits.length > 0 && (
                      <ul className="space-y-2 mb-4">
                        {tier.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500 shrink-0" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <Button
                      className="w-full rounded-full"
                      variant={index === 0 ? "default" : "outline"}
                      onClick={() => handleSubscribe(tier.id)}
                      disabled={subscribing}
                      data-testid={`subscribe-tier-${tier.id || index}`}
                    >
                      {subscribing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>Subscribe for ${tier.price}/mo</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}

              <p className="text-xs text-center text-muted-foreground">
                Cancel anytime. Creator receives 60% of your subscription.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Badge - Show if subscribed */}
      {isSubscribed && subscriptionInfo && (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl p-3 z-40">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-yellow-500" />
            <div className="flex-1">
              <p className="font-semibold text-sm">Subscribed to {profile.display_name}</p>
              <p className="text-xs text-muted-foreground">{subscriptionInfo.tier_name} • Renews monthly</p>
            </div>
          </div>
        </div>
      )}

      {/* Send Coins Modal */}
      <Dialog open={showSendCoinsModal} onOpenChange={setShowSendCoinsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-pink-500" />
              Send Coins to @{profile?.username}
            </DialogTitle>
            <DialogDescription>
              Your balance: <span className="font-bold text-primary">{formatNumber(myCoinsBalance)} coins</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Quick Amounts */}
            <div className="grid grid-cols-3 gap-2">
              {QUICK_AMOUNTS.map((amount) => (
                <Button
                  key={amount}
                  variant={parseInt(coinAmount) === amount ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCoinAmount(amount.toString())}
                  disabled={amount > myCoinsBalance}
                  className="rounded-full"
                >
                  {amount}
                </Button>
              ))}
            </div>
            
            {/* Custom Amount */}
            <div className="relative">
              <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="number"
                placeholder="Enter amount"
                value={coinAmount}
                onChange={(e) => setCoinAmount(e.target.value)}
                className="pl-10 text-center text-lg"
                min="1"
                max={myCoinsBalance}
              />
            </div>
            
            {coinAmount && parseInt(coinAmount) > myCoinsBalance && (
              <p className="text-destructive text-sm text-center">
                Not enough coins! <Link to="/coins" className="underline">Buy more</Link>
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSendCoinsModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendCoins} 
              disabled={sendingCoins || !coinAmount || parseInt(coinAmount) <= 0 || parseInt(coinAmount) > myCoinsBalance}
              className="bg-gradient-to-r from-pink-500 to-violet-500"
            >
              {sendingCoins ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send {coinAmount || 0} Coins
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Booking Modal */}
      <BookingModal
        open={showBookingModal}
        onClose={() => { setShowBookingModal(false); setSelectedService(null); }}
        provider={profile}
        service={selectedService}
        onSuccess={() => {
          toast.success(tr('appointmentCreated'));
          fetchUserServices();
        }}
      />
    </div>
  );
}
